import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Grid from "@mui/material/Grid";
import {
  Card,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  MenuItem,
  Pagination,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import AgriActionButton from "components/AgriTheme/AgriActionButton";
import AgriPanel from "components/AgriTheme/AgriPanel";
import AgriStatCard from "components/AgriTheme/AgriStatCard";
import MDBox from "components/MDBox";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCount = (value) => new Intl.NumberFormat("en-IN").format(Number(value || 0));

const questionInventoryCategories = [
  { key: "mcq", label: "MCQ bank", icon: "quiz", color: "#72d88d" },
  { key: "assertionReason", label: "AR questions", icon: "rule", color: "#5eead4" },
  { key: "previousYear", label: "PYQ", icon: "history_edu", color: "#f8c75c" },
  { key: "agriBoost", label: "AgriBoost", icon: "rocket_launch", color: "#86efac" },
  { key: "testSeries", label: "Test series only", icon: "assignment", color: "#93c5fd" },
  { key: "liveTest", label: "Live test only", icon: "bolt", color: "#fdba74" },
  {
    key: "aiMentorGenerated",
    label: "AI Mentor generated",
    icon: "smart_toy",
    color: "#c4b5fd",
  },
];

const superAdminCards = [
  {
    key: "totalSubjects",
    label: "Subjects",
    icon: "menu_book",
    tone: "linear-gradient(135deg, #1d4b66 0%, #214f6c 100%)",
    helper: "Academic structure",
  },
  {
    key: "totalTopics",
    label: "Topics",
    icon: "hub",
    tone: "linear-gradient(135deg, #dd8e2f 0%, #b86518 100%)",
    helper: "Knowledge map",
  },
  {
    key: "totalLevels",
    label: "Levels",
    icon: "layers",
    tone: "linear-gradient(135deg, #2d6a8d 0%, #17384f 100%)",
    helper: "Difficulty buckets",
  },
  {
    key: "totalArLevels",
    label: "AR Levels",
    icon: "account_tree",
    tone: "linear-gradient(135deg, #387f91 0%, #173f50 100%)",
    helper: "Assertion reason levels",
  },
  {
    key: "totalQuestions",
    label: "Questions",
    icon: "quiz",
    tone: "linear-gradient(135deg, #2f7a42 0%, #183526 100%)",
    helper: "Question bank",
  },
  {
    key: "totalArQuestions",
    label: "AR Questions",
    icon: "rule",
    tone: "linear-gradient(135deg, #1f8f78 0%, #0f3f38 100%)",
    helper: "Assertion reason bank",
  },
  {
    key: "totalAgriBoosts",
    label: "AgriBoost Cards",
    icon: "rocket_launch",
    tone: "linear-gradient(135deg, #16803f 0%, #0e4b2a 100%)",
    helper: "Complete boost card bank",
  },
  {
    key: "totalPyqQuestions",
    label: "PYQ Questions",
    icon: "history_edu",
    tone: "linear-gradient(135deg, #9a6b18 0%, #5c3f09 100%)",
    helper: "Previous year MCQs",
  },
  {
    key: "todayQuestionsAdded",
    label: "MCQ Added Today",
    icon: "post_add",
    tone: "linear-gradient(135deg, #0f766e 0%, #134e4a 100%)",
    helper: "Questions created today",
  },
  {
    key: "todayArQuestionsAdded",
    label: "AR Added Today",
    icon: "playlist_add_check",
    tone: "linear-gradient(135deg, #0d9488 0%, #115e59 100%)",
    helper: "AR questions created today",
  },
  {
    key: "todayAgriBoostsAdded",
    label: "Boost Added Today",
    icon: "add_chart",
    tone: "linear-gradient(135deg, #16a34a 0%, #166534 100%)",
    helper: "AgriBoost cards created today",
  },
  {
    key: "userCount",
    label: "Users",
    icon: "groups",
    tone: "linear-gradient(135deg, #567d35 0%, #2f5a24 100%)",
    helper: "Learners onboarded",
  },
  {
    key: "todaysUsers",
    label: "New Today",
    icon: "trending_up",
    tone: "linear-gradient(135deg, #6b7b37 0%, #405f1b 100%)",
    helper: "Fresh signups",
  },
  {
    key: "totalUniversities",
    label: "Colleges",
    icon: "school",
    tone: "linear-gradient(135deg, #4d6d2a 0%, #253813 100%)",
    helper: "Available colleges",
  },
  {
    key: "totalTestSeries",
    label: "Test Series",
    icon: "library_books",
    tone: "linear-gradient(135deg, #4a57a8 0%, #232c68 100%)",
    helper: "Scheduled sets",
  },
  {
    key: "totalMockTests",
    label: "Mock Tests",
    icon: "assignment",
    tone: "linear-gradient(135deg, #1d82a8 0%, #12435a 100%)",
    helper: "Mock catalog",
  },
  {
    key: "totalLiveTests",
    label: "Live Tests",
    icon: "bolt",
    tone: "linear-gradient(135deg, #8d4a2f 0%, #c96c39 100%)",
    helper: "Live test catalog",
  },
  {
    key: "todayActiveUsers",
    label: "Active Today",
    icon: "person_search",
    tone: "linear-gradient(135deg, #395fa8 0%, #1d3f79 100%)",
    helper: "Unique users attempted tests today",
  },
  {
    key: "todayAppOpens",
    label: "App Opens Today",
    icon: "phone_android",
    tone: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
    helper: "Unique users opened app today",
  },
  {
    key: "todayAiMentorOpens",
    label: "AI Mentor Today",
    icon: "smart_toy",
    tone: "linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)",
    helper: "Unique users opened AI Mentor today",
  },
  {
    key: "todayMcqSolved",
    label: "MCQs Today",
    icon: "fact_check",
    tone: "linear-gradient(135deg, #8b3fa5 0%, #5a2374 100%)",
    helper: "Questions solved today",
  },
  {
    key: "liveTestSeries",
    label: "Live Now",
    icon: "bolt",
    tone: "linear-gradient(135deg, #8d4a2f 0%, #c96c39 100%)",
    helper: "Running now",
  },
];

const employeeCards = [
  {
    key: "totalSubjects",
    label: "Subjects",
    icon: "menu_book",
    tone: "linear-gradient(135deg, #214f6c 0%, #183d54 100%)",
    helper: "Assigned scope",
  },
  {
    key: "totalTopics",
    label: "Topics",
    icon: "hub",
    tone: "linear-gradient(135deg, #dd8e2f 0%, #b86518 100%)",
    helper: "Mapped content",
  },
  {
    key: "uniqueLevels",
    label: "Levels",
    icon: "layers",
    tone: "linear-gradient(135deg, #2d6a8d 0%, #17384f 100%)",
    helper: "Level coverage",
  },
  {
    key: "totalArLevels",
    label: "Your AR Levels",
    icon: "account_tree",
    tone: "linear-gradient(135deg, #387f91 0%, #173f50 100%)",
    helper: "Created by you",
  },
  {
    key: "totalQuestions",
    label: "Questions",
    icon: "quiz",
    tone: "linear-gradient(135deg, #2f7a42 0%, #183526 100%)",
    helper: "Created items",
  },
  {
    key: "totalArQuestions",
    label: "Your AR Questions",
    icon: "rule",
    tone: "linear-gradient(135deg, #1f8f78 0%, #0f3f38 100%)",
    helper: "Created by you",
  },
  {
    key: "totalAgriBoosts",
    label: "Your AgriBoost",
    icon: "rocket_launch",
    tone: "linear-gradient(135deg, #16803f 0%, #0e4b2a 100%)",
    helper: "Created by you",
  },
  {
    key: "todayQuestionsAdded",
    label: "MCQ Added Today",
    icon: "post_add",
    tone: "linear-gradient(135deg, #0f766e 0%, #134e4a 100%)",
    helper: "Questions created today",
  },
  {
    key: "todayArQuestionsAdded",
    label: "Your AR Today",
    icon: "playlist_add_check",
    tone: "linear-gradient(135deg, #0d9488 0%, #115e59 100%)",
    helper: "Added by you today",
  },
  {
    key: "todayAgriBoostsAdded",
    label: "Your Boost Today",
    icon: "add_chart",
    tone: "linear-gradient(135deg, #16a34a 0%, #166534 100%)",
    helper: "Added by you today",
  },
  {
    key: "liveTestSeries",
    label: "Live Tests",
    icon: "bolt",
    tone: "linear-gradient(135deg, #7a4d37 0%, #a8603b 100%)",
    helper: "Currently active",
  },
];

function Dashboard() {
  const [data, setData] = useState({});
  const [notification, setNotification] = useState(false);
  const [notiColor, setNotiColor] = useState("warning");
  const [message, setMessage] = useState("");
  const [activeUsersOpen, setActiveUsersOpen] = useState(false);
  const [activeUsersMode, setActiveUsersMode] = useState("app_open");
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [activeUsersPage, setActiveUsersPage] = useState(1);
  const [activeUsersLimit, setActiveUsersLimit] = useState(10);
  const [activeUsersData, setActiveUsersData] = useState(null);

  const adminDetailsRaw = localStorage.getItem("admin_details");
  const user = adminDetailsRaw ? JSON.parse(adminDetailsRaw) : {};
  const normalizedAdminRole = String(user?.role || "").toLowerCase();
  const isSuperAdmin = normalizedAdminRole === "superadmin" || normalizedAdminRole === "admin";

  const quickLinks = useMemo(() => {
    if (isSuperAdmin) {
      return [
        { label: "Create Subject", path: "/addsubject" },
        { label: "Add Question", path: "/addQuestion" },
        { label: "Create Test Series", path: "/addTestseries" },
        { label: "Support Queue", path: "/support-issues" },
      ];
    }

    return [
      { label: "Topics", path: "/topics" },
      { label: "Questions", path: "/questions" },
      { label: "Support Queue", path: "/support-issues" },
      { label: "Test Series", path: "/testseries" },
    ];
  }, [isSuperAdmin]);

  useEffect(() => {
    const requestUrl = isSuperAdmin
      ? Constants.getUrls.getAdminDasboard
      : Constants.getUrls.getDashboard;

    if (init() === "success") {
      axios
        .get(requestUrl)
        .then((resp) => {
          setData(resp?.data?.data || {});
        })
        .catch((err) => {
          setNotification(true);
          setNotiColor("warning");
          setMessage(err?.response?.data?.message || "Unable to load dashboard data.");
          setTimeout(() => {
            setNotification(false);
            setMessage("");
            setNotiColor("warning");
          }, 3000);
        });
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!activeUsersOpen || !isSuperAdmin) {
      return;
    }

    if (init() !== "success") {
      return;
    }

    setActiveUsersLoading(true);
    axios
      .get(Constants.getUrls.getAdminActiveUsers, {
        params: {
          mode: activeUsersMode,
          page: activeUsersPage,
          limit: activeUsersLimit,
        },
      })
      .then((resp) => {
        setActiveUsersData(resp?.data?.data || null);
      })
      .catch((err) => {
        setNotification(true);
        setNotiColor("warning");
        setMessage(err?.response?.data?.message || "Unable to load active users.");
      })
      .finally(() => {
        setActiveUsersLoading(false);
      });
  }, [activeUsersMode, activeUsersOpen, activeUsersPage, activeUsersLimit, isSuperAdmin]);

  const cards = isSuperAdmin ? superAdminCards : employeeCards;

  return (
    <DashboardLayout>
      <DashboardNavbar title="Dashboard" />

      <Grid container spacing={2.5}>
        {isSuperAdmin ? (
          <Grid item xs={12}>
            <AgriPanel
              title="Complete question inventory"
              subtitle="Every independent question store is counted once, across MCQ, AR, PYQ, learning cards, and test-specific banks."
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4} lg={3}>
                  <MDBox
                    height="100%"
                    p={2.4}
                    borderRadius="22px"
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(31,143,120,0.34) 0%, rgba(11,42,40,0.96) 100%)",
                      border: "1px solid rgba(94, 234, 212, 0.24)",
                      boxShadow: "0 18px 38px rgba(0,0,0,0.2)",
                    }}
                  >
                    <MDTypography variant="button" sx={{ color: "#91efcf" }} fontWeight="bold">
                      TOTAL DATABASE QUESTIONS
                    </MDTypography>
                    <MDTypography
                      variant="h2"
                      mt={0.5}
                      fontWeight="bold"
                      sx={{ color: "#ffffff", lineHeight: 1.05 }}
                    >
                      {formatCount(data?.totalDatabaseQuestions)}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#a9bed4" }}>
                      Available, non-deleted question content
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={8} lg={9}>
                  <MDBox
                    display="grid"
                    gridTemplateColumns={{
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    }}
                    gap={1.25}
                  >
                    {questionInventoryCategories.map((item) => (
                      <MDBox
                        key={item.key}
                        p={1.6}
                        borderRadius="18px"
                        sx={{
                          background: "rgba(13, 29, 47, 0.86)",
                          border: "1px solid rgba(126, 170, 220, 0.13)",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.8} mb={0.45}>
                          <Icon sx={{ color: item.color, fontSize: "1.15rem" }}>{item.icon}</Icon>
                          <MDTypography variant="caption" sx={{ color: "#9db2ca" }} fontWeight="bold">
                            {item.label}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h5" sx={{ color: "#f5f9ff" }} fontWeight="bold">
                          {formatCount(data?.questionInventory?.breakdown?.[item.key])}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                </Grid>
              </Grid>

              <MDTypography variant="caption" display="block" mt={1.5} sx={{ color: "#8299b4" }}>
                Soft-deleted questions are excluded. Test-series assignments, friends-test
                references, AI Mentor copies of bank questions, and submitted-answer snapshots are
                not counted again.
              </MDTypography>
            </AgriPanel>
          </Grid>
        ) : null}

        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={card.key}>
            <AgriStatCard
              icon={card.icon}
              label={card.label}
              value={data?.[card.key] ?? 0}
              tone={card.tone}
              helper={card.helper}
              compact
              onClick={
                isSuperAdmin &&
                ["todayActiveUsers", "todayAppOpens", "todayAiMentorOpens"].includes(card.key)
                  ? () => {
                      setActiveUsersMode(
                        card.key === "todayActiveUsers"
                          ? "active"
                          : card.key === "todayAiMentorOpens"
                            ? "ai_mentor"
                            : "app_open"
                      );
                      setActiveUsersPage(1);
                      setActiveUsersOpen(true);
                    }
                  : undefined
              }
            />
          </Grid>
        ))}

        <Grid item xs={12} lg={7}>
          <AgriPanel
            title="Today’s command center"
            subtitle="Move through the most-used admin workflows without hunting through the menu."
          >
            <MDBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
              gap={1.5}
            >
              {quickLinks.map((item) => (
                <Link key={item.path} to={item.path}>
                  <MDBox
                    px={2.25}
                    py={2.1}
                    borderRadius="22px"
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(18,38,60,0.96) 0%, rgba(11,24,40,0.94) 100%)",
                      border: "1px solid rgba(126, 170, 220, 0.14)",
                      boxShadow: "0 16px 36px rgba(0,0,0,0.18)",
                      transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                      position: "relative",
                      overflow: "hidden",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background:
                          "radial-gradient(circle at top right, rgba(86,176,255,0.18) 0%, rgba(86,176,255,0) 46%)",
                        pointerEvents: "none",
                      },
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.24)",
                        borderColor: "rgba(126, 170, 220, 0.28)",
                      },
                    }}
                  >
                    <MDTypography variant="h6" sx={{ color: "#f4f8ff" }} fontWeight="bold">
                      {item.label}
                    </MDTypography>
                    <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                      Open section quickly
                    </MDTypography>
                  </MDBox>
                </Link>
              ))}
            </MDBox>
          </AgriPanel>
        </Grid>

        <Grid item xs={12} lg={5}>
          <AgriPanel
            title="Workspace pulse"
            subtitle="A lightweight view of what matters right now."
          >
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDBox
                p={2}
                borderRadius="20px"
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(21,128,61,0.18) 0%, rgba(10,30,20,0.88) 100%)",
                  border: "1px solid rgba(72, 187, 120, 0.16)",
                }}
              >
                <MDTypography variant="button" sx={{ color: "#91efb1" }}>
                  Question approvals
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: "#d9e6f7" }}>
                  Review pending approval items from the Questions section whenever needed.
                </MDTypography>
              </MDBox>
              <MDBox
                p={2}
                borderRadius="20px"
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(10,22,40,0.88) 100%)",
                  border: "1px solid rgba(96, 165, 250, 0.16)",
                }}
              >
                <MDTypography variant="button" sx={{ color: "#8dc8ff" }}>
                  Total colleges available
                </MDTypography>
                <MDTypography variant="h4" sx={{ color: "#f4f8ff" }} fontWeight="bold">
                  {data?.totalUniversities ?? data?.totalColleges ?? 0}
                </MDTypography>
              </MDBox>
              <MDBox
                p={2}
                borderRadius="20px"
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(245,158,11,0.18) 0%, rgba(40,22,8,0.88) 100%)",
                  border: "1px solid rgba(251, 191, 36, 0.16)",
                }}
              >
                <MDTypography variant="button" sx={{ color: "#ffd36d" }}>
                  Need attention
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: "#d9e6f7" }}>
                  Review support issues, pending approvals, and live test activity from the left
                  rail.
                </MDTypography>
              </MDBox>
            </MDBox>
          </AgriPanel>
        </Grid>

        <Grid item xs={12}>
          <AgriPanel
            title="Admin rhythm"
            subtitle="The dashboard is now optimized for wider content space with a collapsible sidebar and lighter shell."
            actions={
              <Link to="/support-issues">
                <AgriActionButton color="info">Open Support Desk</AgriActionButton>
              </Link>
            }
          >
            <MDBox
              p={2.2}
              borderRadius="22px"
              sx={{
                background:
                  "linear-gradient(135deg, rgba(17,35,56,0.78) 0%, rgba(12,24,39,0.9) 100%)",
                border: "1px solid rgba(126, 170, 220, 0.12)",
              }}
            >
              <MDTypography variant="button" sx={{ lineHeight: 1.8, color: "#d7e5ff" }}>
              Use the sidebar to switch areas quickly, collapse it when you need maximum working
              space, and manage the highest-impact flows from subjects to support without the old
              template clutter.
              </MDTypography>
            </MDBox>
          </AgriPanel>
        </Grid>
      </Grid>

      <Dialog
        open={activeUsersOpen}
        onClose={() => setActiveUsersOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0, backgroundColor: "#08121f" }}>
          <MDBox
            px={3}
            py={2.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: "1px solid rgba(126, 170, 220, 0.12)",
              background:
                "linear-gradient(135deg, rgba(17,35,56,0.98) 0%, rgba(11,24,40,0.94) 100%)",
            }}
          >
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#f4f8ff" }}>
                {activeUsersMode === "active"
                  ? "Today's Active Users"
                  : activeUsersMode === "ai_mentor"
                    ? "Today's AI Mentor Opens"
                    : "Today's App Opens"}
              </MDTypography>
              <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                {activeUsersMode === "active"
                  ? "Users who attempted tests today, with college name and solved count."
                  : activeUsersMode === "ai_mentor"
                    ? "Users who opened AI Mentor today, with college name and today's solved count."
                    : "Users who opened the app today, with college name and today's solved count."}
              </MDTypography>
            </MDBox>
            <IconButton onClick={() => setActiveUsersOpen(false)} sx={{ color: "#ffffff" }}>
              <CloseIcon />
            </IconButton>
          </MDBox>

          <MDBox p={3}>
            {activeUsersLoading ? (
              <MDTypography variant="h6" sx={{ color: "#f4f8ff" }}>
                Loading active users...
              </MDTypography>
            ) : !(activeUsersData?.docs || []).length ? (
              <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                No active users found for today.
              </MDTypography>
            ) : (
              <>
                <MDBox display="flex" flexDirection="column" gap={1.5}>
                  {activeUsersData.docs.map((userItem, index) => (
                    <Card
                      key={userItem._id || index}
                      sx={{
                        background:
                          "linear-gradient(180deg, rgba(15,31,50,0.94) 0%, rgba(11,22,36,0.94) 100%)",
                        border: "1px solid rgba(126, 170, 220, 0.12)",
                        boxShadow: "0 16px 34px rgba(0,0,0,0.18)",
                      }}
                    >
                      <MDBox p={2}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={3}>
                            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                              User
                            </MDTypography>
                            <Link to={`/user-details/${userItem._id}`}>
                              <MDTypography
                                variant="button"
                                display="block"
                                fontWeight="bold"
                                sx={{ color: "#ffffff", fontSize: "0.95rem" }}
                              >
                                {userItem.name}
                              </MDTypography>
                            </Link>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                              College
                            </MDTypography>
                            <MDTypography variant="button" display="block" sx={{ color: "#d7e5ff" }}>
                              {userItem.college_name || "-"}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                              MCQs Solved
                            </MDTypography>
                            <MDTypography variant="button" display="block" sx={{ color: "#d7e5ff" }}>
                              {userItem.total_mcqs_solved || 0}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                              Attempts
                            </MDTypography>
                            <MDTypography variant="button" display="block" sx={{ color: "#d7e5ff" }}>
                              {userItem.attempt_count || 0}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                              {activeUsersMode === "active"
                                ? "Last Active"
                                : activeUsersMode === "ai_mentor"
                                  ? "Opened AI Mentor At"
                                  : "Opened App At"}
                            </MDTypography>
                            <MDTypography variant="button" display="block" sx={{ color: "#d7e5ff" }}>
                              {formatDate(
                                activeUsersMode === "active"
                                  ? userItem.last_activity_at
                                  : activeUsersMode === "ai_mentor"
                                    ? userItem.opened_ai_mentor_at
                                    : userItem.opened_app_at
                              )}
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>
                  ))}
                </MDBox>

                <MDBox
                  mt={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <MDBox display="flex" alignItems="center" gap={1.25}>
                    <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                      Rows per page
                    </MDTypography>
                    <TextField
                      select
                      size="small"
                      value={activeUsersLimit}
                      onChange={(event) => {
                        setActiveUsersLimit(Number(event.target.value));
                        setActiveUsersPage(1);
                      }}
                      sx={{ width: 82 }}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </TextField>
                    <MDTypography variant="caption" sx={{ color: "#d7e5ff" }}>
                      {activeUsersData?.totalDocs
                        ? `${(activeUsersData.page - 1) * activeUsersData.limit + 1}-${Math.min(
                            activeUsersData.page * activeUsersData.limit,
                            activeUsersData.totalDocs
                          )} of ${activeUsersData.totalDocs}`
                        : "0 records"}
                    </MDTypography>
                  </MDBox>
                  {activeUsersData?.totalPages > 1 ? (
                    <Pagination
                      page={activeUsersData.page}
                      count={activeUsersData.totalPages}
                      variant="outlined"
                      shape="rounded"
                      sx={agriPaginationSx}
                      onChange={(event, page) => setActiveUsersPage(page)}
                    />
                  ) : null}
                </MDBox>
              </>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime="Just now"
        open={notification}
      />
    </DashboardLayout>
  );
}

export default Dashboard;
